import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  ShoppingCart,
  DollarSign,
  Calendar,
  Download,
  Lock
} from 'lucide-react';
import { format, subDays, startOfDay } from 'date-fns';
import toast from 'react-hot-toast';

const COLORS = ['#4F46E5', '#7C3AED', '#EC4899', '#F59E0B', '#10B981'];

const Analytics = () => {
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const [dateRange, setDateRange] = useState('7d');
  const [analyticsData, setAnalyticsData] = useState({
    visitors: [],
    sales: [],
    topProducts: [],
    trafficSources: []
  });
  const [stats, setStats] = useState({
    totalVisitors: 0,
    totalSales: 0,
    totalRevenue: 0,
    conversionRate: 0
  });
  const [loading, setLoading] = useState(true);

  const hasAnalytics = hasFeature('analytics');

  useEffect(() => {
    if (user && hasAnalytics) {
      fetchAnalyticsData();
    }
  }, [user, dateRange, hasAnalytics]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Generate sample data for demo
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const visitors = [];
      const sales = [];
      
      for (let i = days - 1; i >= 0; i--) {
        const date = subDays(new Date(), i);
        visitors.push({
          date: format(date, 'MMM dd'),
          visitors: Math.floor(Math.random() * 500) + 100,
          pageViews: Math.floor(Math.random() * 1500) + 300
        });
        sales.push({
          date: format(date, 'MMM dd'),
          orders: Math.floor(Math.random() * 20) + 5,
          revenue: Math.floor(Math.random() * 1000) + 200
        });
      }

      const topProducts = [
        { name: 'Product A', sales: 145, revenue: 7250 },
        { name: 'Product B', sales: 98, revenue: 4900 },
        { name: 'Product C', sales: 76, revenue: 3800 },
        { name: 'Product D', sales: 54, revenue: 2700 },
        { name: 'Product E', sales: 32, revenue: 1600 }
      ];

      const trafficSources = [
        { name: 'Direct', value: 35 },
        { name: 'Organic Search', value: 30 },
        { name: 'Social Media', value: 20 },
        { name: 'Referral', value: 10 },
        { name: 'Email', value: 5 }
      ];

      setAnalyticsData({ visitors, sales, topProducts, trafficSources });
      
      setStats({
        totalVisitors: visitors.reduce((sum, v) => sum + v.visitors, 0),
        totalSales: sales.reduce((sum, s) => sum + s.orders, 0),
        totalRevenue: sales.reduce((sum, s) => sum + s.revenue, 0),
        conversionRate: 2.4
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!hasAnalytics) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Analytics Locked</h2>
        <p className="text-gray-500 text-center max-w-md mb-6">
          Advanced analytics is available on Professional and Enterprise plans. Upgrade to unlock detailed insights.
        </p>
        <button
          onClick={() => window.location.href = '/plans'}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
        >
          Upgrade Plan
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Visitors',
      value: stats.totalVisitors.toLocaleString(),
      change: '+12.5%',
      trend: 'up',
      icon: Users
    },
    {
      title: 'Total Sales',
      value: stats.totalSales.toString(),
      change: '+8.2%',
      trend: 'up',
      icon: ShoppingCart
    },
    {
      title: 'Revenue',
      value: `$${stats.totalRevenue.toLocaleString()}`,
      change: '+15.3%',
      trend: 'up',
      icon: DollarSign
    },
    {
      title: 'Conversion Rate',
      value: `${stats.conversionRate}%`,
      change: '-0.5%',
      trend: 'down',
      icon: Eye
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500">Track your website performance and sales</p>
        </div>
        <div className="flex gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <button
            onClick={() => toast.success('Report downloaded!')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <div className={`flex items-center gap-1 mt-2 text-sm ${
                  stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span>{stat.change}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <stat.icon className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visitors Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Visitors & Page Views</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analyticsData.visitors}>
              <defs>
                <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" fontSize={12} />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip />
              <Area type="monotone" dataKey="visitors" stroke="#4F46E5" fillOpacity={1} fill="url(#colorVisitors)" />
              <Area type="monotone" dataKey="pageViews" stroke="#7C3AED" fillOpacity={0.3} fill="#7C3AED" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sales Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales & Revenue</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.sales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" fontSize={12} />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip />
              <Line type="monotone" dataKey="orders" stroke="#10B981" strokeWidth={2} />
              <Line type="monotone" dataKey="revenue" stroke="#F59E0B" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.topProducts} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" stroke="#6B7280" fontSize={12} />
              <YAxis dataKey="name" type="category" stroke="#6B7280" fontSize={12} width={80} />
              <Tooltip />
              <Bar dataKey="sales" fill="#4F46E5" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Traffic Sources</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analyticsData.trafficSources}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {analyticsData.trafficSources.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
